<template>
  <div
    :class="[
      'audio-upload-prompt',
      {
        'audio-upload-prompt--loading': isAudioLoading,
        'audio-upload-prompt--drag-over': isDragged,
        'audio-upload-prompt--drag-leave': dragLeave
      }
    ]"
  >
    <audio-icon v-if="!isAudioLoading" class="audio-upload-prompt__cta-icon"/>
    <spinner v-else-if="loadState === 'loading'" class="audio-upload-prompt__spinner"/>
    <div v-else class="audio-upload-prompt__check-icon-wrapper">
      <solid-check-circle class="audio-upload-prompt__check-icon"/>
    </div>
    <p class="audio-upload-prompt__heading">{{ heading }}</p>
    <p class="audio-upload-prompt__text audio-upload-prompt__subheading">{{ subheading }}</p>
    <file-selector :accept="audioAccept" @file-selected="onFileSelected">
      <tiny-button
        v-if="!isAudioLoading"
        class="audio-upload-prompt__choose-file"
        text="Choose file"
        :with-background="true"
      />
    </file-selector>
    <p v-if="!isAudioLoading" class="audio-upload-prompt__text audio-upload-prompt__link"
      @click="onFileSelected(sampleURL)">Use sample track</p>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, toRefs, watch } from 'vue'
import AudioIcon from '@/assets/AudioIcon.svg'
import TinyButton from '@/components/preview/TinyButton.vue'
import SolidCheckCircle from '@/assets/SolidCheckCircle.svg'
import Spinner from '@/components/common/Spinner.vue'
import { AudioLoadState } from '@/audio/types'
import useWidthLimitedFileName from '@/composables/UseWidthLimitedFileName'
import FileSelector from '@/components/common/FileSelector.vue'
import { audioAccept } from '@/static/file-types'

const sampleURL = 'https://static.serato.com/visualizer/audio/Serato-Studio-Golden-Hour.mp3'

const props = defineProps<{
  isDragged: boolean
  loadState: AudioLoadState
  fileName: string
}>()

const {
  isDragged,
  fileName
} = toRefs(props)

const isAudioLoading = computed(() => ['loading', 'loaded'].includes(props.loadState))

// This ref is required to only show the exit animation when dragging has ended. This is only possible to do if we
// activate the animation after the _first_ time that dragging ends - otherwise we end up with the animation on mount
const dragLeave = ref(false)

watch(isDragged, (isDragged) => {
  dragLeave.value = !isDragged
})

const fontSize = ref('14px')
const truncatedFileName = useWidthLimitedFileName(fileName, fontSize)
const heightMediaQuery = window.matchMedia('(min-height: 1080px)')

heightMediaQuery.addEventListener('change', (e) => {
  fontSize.value = e.matches ? '18px' : '14px'
})

const heading = computed(() =>
  isAudioLoading.value
    ? 'Uploading Audio'
    : 'Drop audio here'
)

const subheading = computed(() =>
  isAudioLoading.value
    ? truncatedFileName.value
    : 'mp3 or wav files\n10min | 200MB max'
)

const emits = defineEmits<{
  (event: 'fileSelected', file: File | string, fileName?: string): void
}>()

const onFileSelected = (file: File | string, fileName?: string) => {
  emits('fileSelected', file, fileName)
}

</script>
<style lang="scss">
.audio-upload-prompt {
  $root: "#{&}";
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $white;
  text-align: center;
  padding: 16px;

  &__heading {
    font-family: $font-bold;
    font-size: 20px;
    @media (min-height: 1080px) {
      font-size: 24px;
    }
    line-height: 24px;
    margin-top: 32px;
  }

  &__text {
    font-family: $font-medium;
    font-size: v-bind(fontSize);
    white-space: break-spaces;
  }

  &__subheading {
    line-height: 20px;
    @media (min-height: 1080px) {
      line-height: 28px;
    }
    margin-top: 8px;
  }

  &__link {
    margin-top: 13px;
    color: $link-blue;
    cursor: pointer;
    text-decoration: underline dashed transparent;
    transition: text-decoration 150ms $animationCurve;

    &:hover {
      text-decoration: underline dashed currentColor;
    }
  }

  &__spinner {
    width: 32px;
    height: 32px;
  }

  &__check-icon-wrapper {
    // This wrapper is to ensure that other elements in the layout do not
    // move around while the check icon performs its animation
    position: relative;
    width: 32px;
    height: 32px;
  }

  &__check-icon {
    // Center the icon absolutely - this is because the animation actually exceeds the final size when it is complete
    // Animation goes from 16px to 32px, but exceeds 32px in the middle.
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    animation: check-expand 340ms cubic-bezier(0.38, 0.03, 0.23, 1.66);
  }

  &__choose-file {
    margin-top: 46px;
    @media (min-height: 1080px) {
      font-size: 18px;
    }
  }

  &--drag-over {
    animation: transparency-in 200ms $animationCurve forwards;

    #{$root}__cta-icon {
      animation: rotate-in 200ms $animationCurve forwards;
    }

    #{$root}__choose-file {
      animation: button-in 200ms $animationCurve forwards;
    }
  }

  &--drag-leave {
    animation: transparency-out 200ms $animationCurve forwards;

    #{$root}__cta-icon {
      animation: rotate-out 200ms $animationCurve forwards;
    }

    #{$root}__choose-file {
      animation: button-out 200ms $animationCurve forwards;
    }
  }

  &--loading {
    #{$root}__heading, #{$root}__subheading {
      margin-top: 16px;
    }
  }

  @keyframes rotate-in {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(10deg);
    }
  }

  @keyframes rotate-out {
    0% {
      transform: rotate(10deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }

  @keyframes transparency-in {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0.5;

    }
  }

  @keyframes transparency-out {
    0% {
      opacity: 0.5;

    }
    100% {
      opacity: 1;
    }
  }

  @keyframes button-in {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes button-out {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes check-expand {
    0% {
      width: 16px;
      height: 16px;
    }
    100% {
      width: 32px;
      height: 32px;
    }
  }
}

</style>
